import { useSelector } from 'react-redux';
import countySocioeconomicSource from 'data/sources/countySocioeconomicSource';
import { socioeconomicFactorRadius } from 'utils/socioeconomicScores';
import { IconLayer } from '@deck.gl/layers';
import { CollisionFilterExtension } from '@deck.gl/extensions';
import MapPointer from 'assets/img/map-pointer.png';
import { LAYER_TYPE, isLayerVisible } from 'utils/layerVisibility';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import ReactDOMServer from 'react-dom/server';
import Popover from '../common/Popover';
import { calculateBounds, isWithinBounds } from 'utils/bounds';

export const COUNTY_SOCIOECONOMIC_LAYER_ID = 'countySocioeconomicLayer';

export default function CountySocioeconomicLayer() {
  const [layer, setLayer] = useState();
  const [previousActiveCounty, setPreviousActiveCounty] = useState(null);
  const [previousActiveCompareCounty, setPreviousActiveCompareCounty] = useState(null);
  const [previousSocioeconomicFactor, setPreviousSocioeconomicFactor] = useState(null);
  const [previousYear, setPreviousYear] = useState(null);
  const [previousLongitude, setPreviousLongitude] = useState(null);
  const [previousLatitude, setPreviousLatitude] = useState(null);
  const [previousZoom, setPreviousZoom] = useState(null);
  const { countySocioeconomicLayer } = useSelector((state) => state.carto.layers);
  const source = countySocioeconomicSource(
    countySocioeconomicLayer?.year || 2019,
    countySocioeconomicLayer?.socioeconomicFactor || 'overall_socioeconomic'
  );
  const viewState = useSelector((state) => state.carto.viewState);
  const [searchParams, _setSearchParams] = useSearchParams();

  useEffect(() => {
    const { longitude, latitude, zoom } = viewState;
    const bounds = calculateBounds(longitude, latitude, zoom);

    const visibleData = countySocioeconomicLayer?.data.filter((item) =>
      isWithinBounds(item, bounds)
    );

    if (!countySocioeconomicLayer || !source) {
      return;
    }

    const isSocioeconomicFactorsLayerActive =
      searchParams.get('socioeconomic-layer') !== null
        ? searchParams.get('socioeconomic-layer') === 'true'
        : true;

    if (isSocioeconomicFactorsLayerActive === false) {
      setLayer(null);
      return;
    }

    if (isLayerVisible(LAYER_TYPE.COUNTY, viewState.zoom) === false) {
      setLayer(null);
      return;
    }

    if (
      layer &&
      previousActiveCounty === searchParams.get('active-county') &&
      previousActiveCompareCounty === searchParams.get('active-compare-county') &&
      previousSocioeconomicFactor === searchParams.get('socioeconomic-factor') &&
      previousYear === searchParams.get('year') &&
      previousLongitude === longitude &&
      previousLatitude === latitude &&
      previousZoom === zoom
    ) {
      return;
    }

    if (
      visibleData?.[0]?.[searchParams.get('socioeconomic-factor')] === undefined ||
      visibleData?.[0]?.year?.toString() !== searchParams.get('year')
    ) {
      return;
    }

    const newIconLayer = new IconLayer({
      id: COUNTY_SOCIOECONOMIC_LAYER_ID,
      data: visibleData,
      extensions: [new CollisionFilterExtension()],
      collisionTestProps: {
        sizeScale: 4,
      },
      // pickable: isLayerVisible(LAYER_TYPE.COUNTY, viewState.zoom),
      pickable: true,
      // opacity: isLayerVisible(LAYER_TYPE.COUNTY, viewState.zoom) ? 1 : 0,
      iconAtlas: MapPointer,
      iconMapping: {
        marker: { x: 0, y: 0, width: 96, height: 97 },
      },
      getIcon: () => 'marker',
      sizeScale: 1,
      getSize: (object) => {
        let countyName = object.countyname;
        let stateName = object.statename;

        let isPercentage =
          countySocioeconomicLayer.socioeconomicFactor.includes('percentage');
        let radius =
          isPercentage === true
            ? (object[countySocioeconomicLayer.socioeconomicFactor] / 100) * 5
            : object[countySocioeconomicLayer.socioeconomicFactor];

        if (
          searchParams.get('active-county') === `${countyName}, ${stateName}` ||
          searchParams.get('active-compare-county') === `${countyName}, ${stateName}`
        ) {
          return socioeconomicFactorRadius(radius);
        }

        return searchParams.get('active-county') === null
          ? socioeconomicFactorRadius(radius)
          : 0;
      },
      getPosition: (d) => [d.lng, d.lat],
      onHover: (info) => {
        if (info?.object) {
          let isPercentage =
            countySocioeconomicLayer?.socioeconomicFactor.includes('percentage');

          info.object = {
            html: ReactDOMServer.renderToStaticMarkup(
              <Popover>
                <div className='whitespace-nowrap flex flex-col gap-1'>
                  <div className='text-sm'>
                    {countySocioeconomicLayer.socioeconomicFactorLabel}:{' '}
                    <span className='text-blue-300'>
                      {Math.round(
                        info.object[countySocioeconomicLayer.socioeconomicFactor] * 100
                      ) / 100}
                      {isPercentage === true ? '%' : ''}
                    </span>
                  </div>
                </div>
              </Popover>
            ),
            style: {
              background: 'transparent',
            },
          };
        }
      },
      updateTriggers: {
        // opacity: [viewState],
        getSize: [
          searchParams.get('active-county'),
          searchParams.get('active-compare-county'),
          searchParams.get('socioeconomic-factor'),
          searchParams.get('year'),
        ],
      },
    });

    setLayer(newIconLayer);
    setPreviousActiveCounty(searchParams.get('active-county'));
    setPreviousActiveCompareCounty(searchParams.get('active-compare-county'));
    setPreviousSocioeconomicFactor(searchParams.get('socioeconomic-factor'));
    setPreviousYear(searchParams.get('year'));
    setPreviousLongitude(longitude);
    setPreviousLatitude(latitude);
    setPreviousZoom(zoom);
  }, [
    countySocioeconomicLayer,
    layer,
    previousActiveCompareCounty,
    previousActiveCounty,
    previousLatitude,
    previousLongitude,
    previousSocioeconomicFactor,
    previousYear,
    previousZoom,
    searchParams,
    source,
    viewState,
    viewState.zoom,
  ]);

  return layer;
}
